/**
 * CurveEditor - Shared inline 2D curve-editing canvas widget
 *
 * Extracted from StrokeModifierPanel so the same editor drives the
 * per-stroke modifier channels AND the chair curve's length profile.
 * Horizontal axis = the curve parameter (t or s, 0..1); vertical axis =
 * the value. 2-5 draggable control points, Catmull-Rom interpolated with
 * the same evaluator resolution uses (StrokeStore.evaluateCurve).
 *
 * The widget owns the canvas, pointer interaction, drawing, and
 * add/removePoint operations. The host panel owns any channel buttons and
 * calls addPoint()/removePoint()/draw() and setRange() as needed.
 *
 * Points are PLAIN data objects; key names are configurable (e.g.
 * {t, v} for modifier channels, {s, w} for the length profile).
 */
import { evaluateCurve } from '../core/StrokeStore.js';

export class CurveEditor {
    /**
     * @param {Object} opts
     * @param {Function} opts.getPoints - () => Array of control points (or null to disable)
     * @param {Function} opts.setPoints - (points) => void, write a DEEP copy back
     * @param {number} opts.min - bottom of the value range
     * @param {number} opts.max - top of the value range
     * @param {number} opts.identity - value of the dashed identity line
     * @param {string} [opts.sKey='t'] - key of the horizontal parameter
     * @param {string} [opts.vKey='v'] - key of the value
     * @param {number} [opts.maxPoints=5]
     * @param {number} [opts.minPoints=2]
     * @param {Function} [opts.getTicks] - () => Array<number> vertical tick positions
     * @param {Function} [opts.onGestureStart] - called ONCE per drag/add/remove (undo snapshot)
     * @param {Function} [opts.onChangeThrottled] - called during drags (host throttles regen)
     * @param {Function} [opts.onCommit] - called at drag end / after add/remove
     */
    constructor(opts) {
        this.opts = {
            sKey: 't',
            vKey: 'v',
            maxPoints: 5,
            minPoints: 2,
            getTicks: () => [],
            onGestureStart: () => {},
            onChangeThrottled: () => {},
            onCommit: () => {},
            ...opts
        };
        this.dragIndex = -1;

        this.canvas = document.createElement('canvas');
        this.canvas.width = 480;  // 2x for crisp rendering
        this.canvas.height = 220;
        this.canvas.style.cssText = `
            width: 100%; height: 110px; border-radius: 8px; touch-action: none;
            border: 1px solid rgba(0,0,0,0.12); background: rgba(0,0,0,0.03); cursor: crosshair;`;
        this._bindEvents();
    }

    get element() {
        return this.canvas;
    }

    get isDragging() {
        return this.dragIndex >= 0;
    }

    /** Change the value range/identity (e.g. on channel switch) and redraw */
    setRange({ min, max, identity }) {
        this.opts.min = min;
        this.opts.max = max;
        this.opts.identity = identity;
        this.dragIndex = -1;
        this.draw();
    }

    // --- Normalized <-> canvas mapping -----------------------------------

    _sv(p) {
        return { s: p[this.opts.sKey], v: p[this.opts.vKey] };
    }

    _evalPoints(points) {
        const { sKey, vKey } = this.opts;
        return points.map(p => ({ t: p[sKey], v: p[vKey] }));
    }

    _toCanvas(s, v) {
        const { min, max } = this.opts;
        const W = this.canvas.width, H = this.canvas.height;
        const PAD = 14;
        return {
            x: PAD + s * (W - 2 * PAD),
            y: H - PAD - ((v - min) / (max - min)) * (H - 2 * PAD)
        };
    }

    _fromCanvas(x, y) {
        const { min, max } = this.opts;
        const W = this.canvas.width, H = this.canvas.height;
        const PAD = 14;
        return {
            s: Math.min(1, Math.max(0, (x - PAD) / (W - 2 * PAD))),
            v: min + ((H - PAD - y) / (H - 2 * PAD)) * (max - min)
        };
    }

    _eventToCanvas(e) {
        const rect = this.canvas.getBoundingClientRect();
        return {
            x: (e.clientX - rect.left) / rect.width * this.canvas.width,
            y: (e.clientY - rect.top) / rect.height * this.canvas.height
        };
    }

    // --- Point operations --------------------------------------------------

    _clonePoints() {
        const pts = this.opts.getPoints();
        return pts ? pts.map(p => ({ ...p })) : null;
    }

    /** Insert a point in the middle of the widest parameter gap */
    addPoint() {
        const pts = this._clonePoints();
        if (!pts || pts.length >= this.opts.maxPoints) return;
        this.opts.onGestureStart();
        const { sKey, vKey } = this.opts;
        const sorted = [...pts].sort((a, b) => a[sKey] - b[sKey]);
        let gapI = 0, gap = -1;
        for (let i = 0; i < sorted.length - 1; i++) {
            const g = sorted[i + 1][sKey] - sorted[i][sKey];
            if (g > gap) { gap = g; gapI = i; }
        }
        const s = (sorted[gapI][sKey] + sorted[gapI + 1][sKey]) / 2;
        pts.push({ [sKey]: s, [vKey]: evaluateCurve(this._evalPoints(pts), s) });
        this.opts.setPoints(pts);
        this.opts.onCommit();
        this.draw();
    }

    /** Remove an interior point (keeps the endpoints) */
    removePoint() {
        const pts = this._clonePoints();
        if (!pts || pts.length <= this.opts.minPoints) return;
        this.opts.onGestureStart();
        const { sKey } = this.opts;
        const sorted = [...pts].sort((a, b) => a[sKey] - b[sKey]);
        const victim = sorted[Math.floor(sorted.length / 2)];
        pts.splice(pts.indexOf(victim), 1);
        this.opts.setPoints(pts);
        this.opts.onCommit();
        this.draw();
    }

    // --- Interaction ---------------------------------------------------------

    _bindEvents() {
        this.canvas.addEventListener('pointerdown', (e) => {
            const pts = this.opts.getPoints();
            if (!pts) return;
            const c = this._eventToCanvas(e);
            let best = -1, bestD = 20;
            pts.forEach((p, i) => {
                const { s, v } = this._sv(p);
                const cp = this._toCanvas(s, v);
                const d = Math.hypot(cp.x - c.x, cp.y - c.y);
                if (d < bestD) { bestD = d; best = i; }
            });
            if (best === -1) return;
            this.opts.onGestureStart();
            this.dragIndex = best;
            try { this.canvas.setPointerCapture(e.pointerId); } catch (_) { /* synthetic */ }
            e.preventDefault();
            e.stopPropagation();
        });

        this.canvas.addEventListener('pointermove', (e) => {
            if (this.dragIndex < 0) return;
            const pts = this._clonePoints();
            if (!pts) return;
            const { sKey, vKey, min, max } = this.opts;
            const p = pts[this.dragIndex];
            if (!p) return;

            const c = this._eventToCanvas(e);
            const f = this._fromCanvas(c.x, c.y);

            // Endpoints stay pinned in the parameter; interior points move
            // but never cross their neighbours
            const sortedS = pts.map(q => q[sKey]).sort((a, b) => a - b);
            const isFirst = p[sKey] === sortedS[0];
            const isLast = p[sKey] === sortedS[sortedS.length - 1];
            if (!isFirst && !isLast) {
                const others = pts.filter(q => q !== p).map(q => q[sKey]).sort((a, b) => a - b);
                const below = others.filter(s => s < p[sKey]).pop() ?? 0;
                const above = others.find(s => s > p[sKey]) ?? 1;
                p[sKey] = Math.min(above - 0.02, Math.max(below + 0.02, f.s));
            }
            p[vKey] = Math.min(max, Math.max(min, f.v));

            this.opts.setPoints(pts);
            this.draw();
            this.opts.onChangeThrottled();
            e.preventDefault();
        });

        const finish = () => {
            if (this.dragIndex < 0) return;
            this.dragIndex = -1;
            this.opts.onCommit();
            this.draw();
        };
        this.canvas.addEventListener('pointerup', finish);
        this.canvas.addEventListener('pointercancel', finish);
    }

    // --- Drawing ----------------------------------------------------------------

    draw() {
        const ctx = this.canvas.getContext('2d');
        const W = this.canvas.width, H = this.canvas.height;
        const PAD = 14;
        ctx.clearRect(0, 0, W, H);

        const pts = this.opts.getPoints();
        if (!pts) return;
        const { min, max, identity } = this.opts;

        // Identity line
        const idY = this._toCanvas(0, identity).y;
        ctx.strokeStyle = 'rgba(0,0,0,0.15)';
        ctx.lineWidth = 1;
        ctx.setLineDash([4, 4]);
        ctx.beginPath();
        ctx.moveTo(PAD, idY);
        ctx.lineTo(W - PAD, idY);
        ctx.stroke();
        ctx.setLineDash([]);

        // Vertical tick marks (e.g. per-layer t positions)
        ctx.strokeStyle = 'rgba(0,0,0,0.10)';
        for (const t of this.opts.getTicks() || []) {
            const x = this._toCanvas(t, 0).x;
            ctx.beginPath();
            ctx.moveTo(x, PAD);
            ctx.lineTo(x, H - PAD);
            ctx.stroke();
        }

        // Curve
        const evalPts = this._evalPoints(pts);
        ctx.strokeStyle = '#00b4dc';
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        for (let i = 0; i <= 60; i++) {
            const s = i / 60;
            const v = Math.min(max, Math.max(min, evaluateCurve(evalPts, s)));
            const c = this._toCanvas(s, v);
            if (i === 0) ctx.moveTo(c.x, c.y);
            else ctx.lineTo(c.x, c.y);
        }
        ctx.stroke();

        // Control points
        for (const p of pts) {
            const { s, v } = this._sv(p);
            const c = this._toCanvas(s, v);
            ctx.beginPath();
            ctx.arc(c.x, c.y, 7, 0, Math.PI * 2);
            ctx.fillStyle = '#fff';
            ctx.fill();
            ctx.lineWidth = 2.5;
            ctx.strokeStyle = '#0090b0';
            ctx.stroke();
        }
    }
}
